// ─── DataAnalyticsView ────────────────────────────────────────────────────────
// Top-level container for the Dashboard → Data Analytics workspace.
// Renders the sub-tab header (Visualize / Validate / Formulas with the
// XD green underline) plus the shared date-range controls, then dispatches
// to the active sub-tab component.
//
// This component is intentionally thin — all sub-tab implementation lives
// in sibling files so each XD screen has its own focused module.
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from 'react'
import { Calendar } from 'lucide-react'
import VisualizeTab from './VisualizeTab'
import ValidateTab from './ValidateTab'
import FormulasTab from './FormulasTab'
import DateTimePicker from '../../common/DateTimePicker'

const SUB_TABS = ['Visualize', 'Validate', 'Formulas']

export default function DataAnalyticsView({
    projectId = null,
    currentDataAnalyticsIndex,
    setCurrentDataAnalyticsIndex,
    dateRangeGraph,
    setDateRangeGraph,
    activePeriod,
    handlePeriodClick,
    selectedParameters = [],
    restrictedSubTabIndexes = new Set(),
}) {
    // Shared refresh counter — bumped by the toolbar's refresh icon and
    // threaded into each sub-tab so its useVisualizeData call and any chart
    // re-fetch from the server. Lives here (in the shared parent) instead of
    // duplicating one inside every tab, so a single click refreshes both the
    // active grid and its chart without us having to plumb the function
    // through the per-tab toolbar.
    const [refreshNonce, setRefreshNonce] = useState(0)
    const triggerRefresh = () => setRefreshNonce((n) => n + 1)

    // Mobile only: the D/W/M/Y + Start/End date controls don't fit the header on
    // a phone, so they collapse behind a calendar icon that opens this popover.
    const [dateMenuOpen, setDateMenuOpen] = useState(false)
    const dateMenuRef = useRef(null)
    // Portal target for the active sub-tab's ⋮ overflow menu, so on mobile it
    // sits in the tab-row group beside the calendar (matches the XD) instead of
    // in the chart toolbar below.
    const [menuSlot, setMenuSlot] = useState(null)
    useEffect(() => {
        if (!dateMenuOpen) return
        const close = (e) => { if (dateMenuRef.current && !dateMenuRef.current.contains(e.target)) setDateMenuOpen(false) }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [dateMenuOpen])

    // The date controls (period picker + Start/End inputs) — shared by the
    // desktop inline layout and the mobile calendar popover so they can't drift.
    const dateControls = (
        <>
            <div className="flex items-center gap-1 border border-gray-300 rounded py-0.5 px-1">
                {['D', 'W', 'M', 'Y'].map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => handlePeriodClick(p)}
                        className={`common-size15 px-3 py-2 text-xs font-semibold rounded transition ${
                            activePeriod === p
                                ? 'bg-[#888888] text-white'
                                : 'text-[#888888] hover:bg-gray-100'
                        }`}
                    >
                        {p}
                    </button>
                ))}
            </div>
            <div className="flex items-end gap-2 text-xs">
                <div className="flex flex-col text-[10px] text-gray-500">
                    <span>Start Date</span>
                    <DateTimePicker
                        value={dateRangeGraph?.from}
                        defaultTime="00:00:00"
                        ariaLabel="Start Date"
                        onChange={(val) => setDateRangeGraph((prev) => ({ ...prev, from: val }))}
                    />
                </div>
                <div className="flex flex-col text-[10px] text-gray-500">
                    <span>End Date</span>
                    <DateTimePicker
                        value={dateRangeGraph?.to}
                        defaultTime="23:59:59"
                        ariaLabel="End Date"
                        onChange={(val) => setDateRangeGraph((prev) => ({ ...prev, to: val }))}
                    />
                </div>
            </div>
        </>
    )

    return (
        <div className="w-full h-full flex rounded-lg flex-col bg-white">
            {/* Sub-tab header with date controls */}
            <div className="flex items-center justify-between border[#F1F2F4] px-4 pt-2 pb-2 mb-1">
                <div className="flex gap-8">
                    {SUB_TABS.map((label, index) => {
                        const active = index === currentDataAnalyticsIndex
                        const restricted = restrictedSubTabIndexes.has(index)
                        return (
                            <button
                                key={label}
                                type="button"
                                disabled={restricted}
                                title={restricted ? 'Not available for collaborators' : undefined}
                                onClick={restricted ? undefined : () => setCurrentDataAnalyticsIndex(index)}
                                className={`text-sm pb-1 md:pb-2 border-b-4 transition ${
                                    restricted
                                        ? 'border-transparent text-gray-300 cursor-not-allowed'
                                        : active
                                            ? 'border-[#56B565]'
                                            : 'border-transparent text-gray-700 hover:text-gray-900'
                                }`}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>

                {/* Desktop: inline D/W/M/Y + Start/End date controls. items-end
                    so the picker and the date inputs share the same baseline. */}
                <div className="hidden md:flex items-end gap-3 [&>div:first-child]:mr-4">
                    {dateControls}
                </div>

                {/* Mobile: a bordered group holding the calendar icon (opens the
                    date controls in a popover) and, beside it, the active sub-tab's
                    ⋮ overflow menu (portalled into menuSlot) — matches the XD. */}
                <div className="md:hidden flex items-center gap-0.5 md:border border-[#D6D6D6] rounded-lg px-1 py-0.5 bg-white">
                    <div className="relative" ref={dateMenuRef}>
                        <button
                            type="button"
                            onClick={() => setDateMenuOpen((o) => !o)}
                            className="p-1.5 rounded hover:bg-gray-100 text-[#4D4D4D]"
                            title="Date range"
                            aria-label="Date range"
                        >
                            <Calendar className="w-5 h-5" />
                        </button>
                        {dateMenuOpen && (
                            <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex flex-col gap-3 min-w-[220px]">
                                {dateControls}
                            </div>
                        )}
                    </div>
                    <span className="w-px h-5 bg-[#E5E7EB]" />
                    <div ref={setMenuSlot} className="relative flex items-center" />
                </div>
            </div>

            {/* Active sub-tab body */}
            {currentDataAnalyticsIndex === 0 && (
                <VisualizeTab
                    projectId={projectId}
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                    onPeriodChange={handlePeriodClick}
                    externalRefreshNonce={refreshNonce}
                    mobileMenuSlot={menuSlot}
                />
            )}
            {currentDataAnalyticsIndex === 1 && (
                <ValidateTab
                    projectId={projectId}
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                    externalRefreshNonce={refreshNonce}
                    mobileMenuSlot={menuSlot}
                />
            )}
            {currentDataAnalyticsIndex === 2 && (
                <FormulasTab
                    selectedParameters={selectedParameters}
                    dateRangeGraph={dateRangeGraph}
                    activePeriod={activePeriod}
                    externalRefreshNonce={refreshNonce}
                    mobileMenuSlot={menuSlot}
                />
            )}
        </div>
    )
}
